import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useSelectedFranchisee } from '../context/SelectedFranchiseeContext';
import createApiInstance from '../utilities/apiAuth';
import axios from 'axios';
import Swal from 'sweetalert2';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Function to format email display
const formatEmailDisplay = (email) => {
  if (!email) return '';
  
  // Extract the part before @
  const namePart = email.split('@')[0];
  
  // If the name contains dots, format it as "First Last"
  if (namePart.includes('.')) {
    return namePart
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
  
  // Otherwise just capitalize the first letter
  return namePart.charAt(0).toUpperCase() + namePart.slice(1);
};

export default function Navbar() {
  const { user, fetchUserData } = useUser();
  const { clearSelectedFranchisee } = useSelectedFranchisee();
  const navigate = useNavigate();
  const location = useLocation();
  const api = createApiInstance();
  const [scrolled, setScrolled] = useState(false);

  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const isActive = (href) => {
    return location.pathname === href;
  }

  const navigation = [
    { name: 'Home', href: '/', current: true },
    ...(user.role === 'admin' ? [{ name: 'Clients', href: '/clients', current: false }] : []),
    ...(user.role === 'admin' ? [{ name: 'Franchisees', href: '/tws_franchisee', current: false }] : []),
    ...(user.role === 'admin' ? [{ name: 'Users', href: '/users', current: false }] : []),
    { name: 'EOD Records', href: '/eod', current: false },
    ...(user.role === 'admin' ? [{ name: 'Analytics', href: '/analytics', current: false }] : []),
    { name: 'Versions', href: '/version', current: false },
  ]

  // Erase the localstorage before logout 
  const handleLogOut = async () => {
    
    try {
      const res = await api.post(`/session/terminate`);
      console.log(res.data.message);
    } catch (error) {
      console.error('Error ending session:', error);
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('selectedFranchisee');
    localStorage.removeItem('viewedPages');
    localStorage.removeItem('selectedClient');
    clearSelectedFranchisee();
    navigate('/magic-link-request');
  }

  const handleRCSetup = async () => {
    // Show input dialog for email and password
    const { value: formValues } = await Swal.fire({
      title: '<div class="flex items-center space-x-3"><svg class="w-6 h-6 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg><span class="text-xl font-semibold text-gray-800">RingCentral Setup</span></div>',
      html: `
        <div class="max-w-md mx-auto">
          <!-- Setup Information -->
          <div class="bg-hs-blue/10 border border-hs-blue/20 rounded-lg p-4 mb-6">
            <div class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-hs-blue mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <h4 class="font-medium text-hs-blue text-left">Setup Required</h4>
                <p class="text-sm text-left text-hs-blue/80 mt-1">Enter your RingCentral credentials to automate your Ringcentral Recordings and call logs.</p>
              </div>
            </div>
          </div>

          <!-- Credentials Form -->
          <div class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-left text-gray-700 mb-2">RingCentral Email</label>
              <div class="relative">
                <input
                  id="email"
                  type="email"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-hs-blue focus:border-hs-blue transition-colors duration-200"
                  placeholder="name@tempwallsystems.com"
                  value="${user.external_email ?? ''}"
                  required
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"></path>
                  </svg>
                </div>
              </div>
            </div>
            
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2 text-left">RingCentral Password</label>
              <div class="relative">
                <input
                  id="password"
                  type="password"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-hs-blue focus:border-hs-blue transition-colors duration-200"
                  placeholder="Enter your password"
                  required
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Security Note -->
          <div class="mt-4 text-center">
            <p class="text-xs text-gray-500">Your credentials are encrypted and securely transmitted</p>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: `
    <span style="display: inline-flex; align-items: center;">
      <svg class="w-4 h-4 mr-2" 
           style="display:inline-block;vertical-align:middle;" 
           fill="none" stroke="currentColor" 
           viewBox="0 0 24 24">
        <path stroke-linecap="round" 
              stroke-linejoin="round" 
              stroke-width="2" 
              d="M13 10V3L4 14h7v7l9-11h-7z">
        </path>
      </svg>
      Start Setup
    </span>`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#0091AE',
      width: '28rem',
      customClass: {
        popup: 'rounded-xl shadow-2xl',
        confirmButton: 'bg-hs-blue hover:bg-hs-blue/90 focus:ring-hs-blue',
        cancelButton: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
      },
      focusConfirm: false,
      preConfirm: () => {
        const email = document.getElementById('email').value;
        const password = document.getElementById('password').value;
        
        if (!email || !password) {
          Swal.showValidationMessage('Please fill in both email and password');
          return false;
        }
        
        if (!email.includes('@')) {
          Swal.showValidationMessage('Please enter a valid email address');
          return false;
        }
        
        return { email, password };
      }
    });

    if (!formValues) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/rc-setup/start-request`,
        {
          email: formValues.email,
          password: formValues.password,
          eopsEmail: user.email
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      if (response.data.ok) {
        Swal.fire({
          title: '<div class="flex items-center space-x-3"><svg class="w-6 h-6 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg><span class="text-xl font-semibold text-gray-800">Setup Initiated!</span></div>',
          html: `
            <div class="text-center">
              <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-hs-blue/10 mb-4">
                <svg class="h-6 w-6 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Enter OTP Code</h3>
              <p class="text-sm text-gray-600 mb-4">Please enter the OTP code sent to your email inbox to complete the RingCentral automation setup.</p>
              <div class="bg-gray-50 rounded-lg p-3">
                <label class="block text-xs font-medium text-gray-700 mb-2 text-left">OTP Code:</label>
                <input type="text" id="otp-input" class="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-hs-blue focus:border-transparent" placeholder="000000" maxlength="6" autocomplete="off">
              </div>
            </div>
          `,
           confirmButtonText: 'Submit OTP',
           confirmButtonColor: '#0091AE',
           width: '28rem',
           customClass: {
             popup: 'rounded-xl shadow-2xl'
           },
           preConfirm: () => {
             const otpValue = document.getElementById('otp-input').value;
             if (!otpValue || otpValue.length !== 6) {
               Swal.showValidationMessage('Please enter a valid 6-digit OTP code');
               return false;
             }
             return otpValue;
           }
                 }).then(async (result) => {
           if (result.isConfirmed && result.value) {
             try {
               // Send OTP and correlation ID to backend
               const otpResponse = await axios.post(
                 `${process.env.REACT_APP_BACKEND_URL}/api/rc-setup/store-otp`,
                 {
                   otp: result.value,
                   correlationId: response.data.correlationId
                 },
                 {
                   headers: {
                     'Content-Type': 'application/json',
                     'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                   }
                 }
               );

               console.log(otpResponse);

               if (otpResponse.status === 200 && otpResponse.data.message === 'OTP Stored Successfully') {
                 // Show confirmation that OTP was sent to server
                 Swal.fire({
                   title: '<div class="flex items-center space-x-3"><svg class="w-6 h-6 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg><span class="text-xl font-semibold text-gray-800">OTP Submitted!</span></div>',
                   html: `
                     <div class="text-center">
                       <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                         <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                         </svg>
                       </div>
                       <h3 class="text-lg font-medium text-gray-900 mb-2">OTP Code Sent to Server</h3>
                       <p class="text-sm text-gray-600 mb-4">Your OTP code has been successfully submitted and is being processed. You'll receive a confirmation once the RingCentral setup is complete.</p>
                       <div class="bg-blue-50 rounded-lg p-3 text-left">
                         <div class="flex items-start space-x-2">
                           <svg class="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                           </svg>
                           <p class="text-xs text-blue-700">The system is now verifying your OTP and completing the RingCentral integration setup.</p>
                         </div>
                       </div>
                     </div>
                   `,
                   confirmButtonText: 'Great!',
                   confirmButtonColor: '#0091AE',
                   width: '28rem',
                   customClass: {
                     popup: 'rounded-xl shadow-2xl'
                   }
                 });
               } else {
                 throw new Error('Failed to submit OTP to server');
               }
             } catch (error) {
               console.error('OTP submission error:', error);
               Swal.fire({
                 title: '<div class="flex items-center space-x-3"><svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path></svg><span class="text-xl font-semibold text-gray-800">OTP Submission Failed</span></div>',
                 html: `
                   <div class="text-center">
                     <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                       <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                       </svg>
                     </div>
                     <h3 class="text-lg font-medium text-gray-900 mb-2">OTP Could Not Be Submitted</h3>
                     <p class="text-sm text-gray-600 mb-4">There was a problem submitting your OTP code to the server. Please try again.</p>
                     <div class="bg-red-50 rounded-lg p-3 text-left">
                       <div class="flex items-start space-x-2">
                         <svg class="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                         </svg>
                         <p class="text-xs text-red-700">Please check your connection and try submitting the OTP again.</p>
                       </div>
                     </div>
                   </div>
                 `,
                 confirmButtonText: 'Try Again',
                 confirmButtonColor: '#0091AE',
                 width: '28rem',
                 customClass: {
                   popup: 'rounded-xl shadow-2xl'
                 }
               });
             }
           }
         });
       } else {
         throw new Error(response.data.error || 'Unknown error occurred');
       }
    } catch (error) {
      console.error('RC Setup error:', error);
      
      let errorMessage = 'Failed to start RingCentral setup';
      if (error.response?.data?.detail) {
        errorMessage += `: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      Swal.fire({
        title: '<div class="flex items-center space-x-3"><svg class="w-6 h-6 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path></svg><span class="text-xl font-semibold text-gray-800">Setup Failed</span></div>',
        html: `
          <div class="text-center">
            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-hs-blue/10 mb-4">
              <svg class="h-6 w-6 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Setup Could Not Be Started</h3>
            <p class="text-sm text-gray-600 mb-4">${errorMessage}</p>
            <div class="bg-hs-blue/5 border border-hs-blue/20 rounded-lg p-3 text-left">
              <div class="flex items-start space-x-2">
                <svg class="w-4 h-4 text-hs-blue mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p class="text-xs text-hs-blue/80">Please verify your credentials and try again. If the problem persists, contact support.</p>
              </div>
            </div>
          </div>
        `,
        confirmButtonText: 'Try Again',
        confirmButtonColor: '#0091AE',
        width: '28rem',
        customClass: {
          popup: 'rounded-xl shadow-2xl'
        }
      });
    }
  };

  const handleAccountSettings = () => {
    Swal.fire({
      title: '<div class="flex items-center space-x-3"><svg class="w-6 h-6 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg><span class="text-xl font-semibold text-gray-800">Account Settings</span></div>',
      html: `
        <div class="max-w-md mx-auto">
          <!-- Profile Information Section -->
          <div class="bg-gradient-to-r from-hs-blue/10 to-hs-blue/5 rounded-lg p-4 mb-6 border border-hs-blue/20">
            <div class="flex items-center space-x-3">
              <div class="w-12 h-12 bg-hs-blue rounded-full flex items-center justify-center">
                <span class="text-white font-semibold text-lg">${user.email ? user.email.charAt(0).toUpperCase() : 'U'}</span>
              </div>
              <div class="flex flex-col justify-start">
                <h3 class="font-medium text-gray-900">${user.email || 'User'}</h3>
                <p class="text-left text-sm text-gray-600">${user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase() : 'User'} Account</p>
              </div>
            </div>
          </div>

          <!-- Email Settings Section -->
          <div class="bg-white rounded-lg border border-gray-200 p-5 mb-6 shadow-sm">
            <div class="flex items-center space-x-2 mb-4">
              <svg class="w-5 h-5 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              <h4 class="text-lg font-medium text-gray-900">Email Settings</h4>
            </div>
            
            <div class="space-y-3">
              <div>
                <label for="external-email" class="block text-sm font-medium text-gray-700 mb-1 text-left">External Email Address</label>
                <div class="relative">
                  <input
                    id="external-email"
                    type="email"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-hs-blue focus:border-hs-blue transition-colors duration-200"
                    placeholder="Enter your external email"
                    value="${user.external_email ?? ''}"
                  />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"></path>
                    </svg>
                  </div>
                </div>
                <p class="mt-1 text-xs text-left text-gray-500">This email will be used for external communications</p>
              </div>
            </div>
          </div>

          <!-- Integrations Section -->
          <div class="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div class="flex items-center space-x-2 mb-4">
              <svg class="w-5 h-5 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
              <h4 class="text-lg font-medium text-gray-900">Integrations</h4>
            </div>
            
            <div class="space-y-4">
              <!-- RingCentral Integration -->
              <div class="border border-gray-200 rounded-lg p-4 hover:border-hs-blue/40 transition-colors duration-200">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-3">
                    <div class="w-10 h-10 bg-hs-blue/10 rounded-lg flex items-center justify-center">
                      <svg class="w-5 h-5 text-hs-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <div>
                      <h5 class="font-medium text-gray-900 text-left">RingCentral</h5>
                      <p class="text-sm text-gray-500">Phone system integration</p>
                    </div>
                  </div>
                  <button
                    id="rc-setup-btn"
                    class="inline-flex items-center px-4 py-2 bg-hs-blue hover:bg-hs-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hs-blue text-white text-sm font-medium rounded-md transition-all duration-200 transform hover:scale-105 active:scale-95"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Setup
                  </button>
                </div>
              </div>

              <!-- Future Integration Placeholder -->
              <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                  </div>
                  <div>
                    <h5 class="font-medium text-gray-500 text-left">More Integrations</h5>
                    <p class="text-sm text-gray-400 text-left">Coming soon...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Help Text -->
          <div class="mt-4 text-center">
            <p class="text-xs text-gray-500">Need help? Contact your system administrator</p>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: '<span style="display: inline-flex; align-items: center;"><svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>Save Changes</span>',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#0091AE',
      width: '32rem',
      customClass: {
        popup: 'rounded-xl shadow-2xl',
        confirmButton: 'bg-hs-blue hover:bg-hs-blue/90 focus:ring-hs-blue',
        cancelButton: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
      },
      didOpen: () => {
        // Add click handler for RC Setup button
        const rcBtn = document.getElementById('rc-setup-btn');
        if (rcBtn) {
          rcBtn.addEventListener('click', handleRCSetup);
        }
      },
      preConfirm: () => {
        const email = (document.getElementById('external-email')).value;
        if (!email) {
          Swal.showValidationMessage('Please enter a valid email address');
          return false;
        }
        if (!email.includes('@')) {
          Swal.showValidationMessage('Please enter a valid email address');
          return false;
        }
        return email;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('authToken');
          const userId = user._id || user.id;

          // Send PUT request to update the external email
          await api.put(`/user/${userId}/update-external-email`, {
            external_email: result.value
          }, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          await fetchUserData();

          Swal.fire({
            title: 'Success!',
            text: 'Your external email has been updated successfully.',
            icon: 'success',
            confirmButtonText: 'Great!',
            confirmButtonColor: '#0091AE',
            customClass: {
              popup: 'rounded-xl shadow-2xl'
            }
          });
        } catch (error) {
          Swal.fire({
            title: 'Update Failed',
            text: 'There was a problem updating your email. Please try again.',
            icon: 'error',
            confirmButtonText: 'Try Again',
            confirmButtonColor: '#0091AE',
            customClass: {
              popup: 'rounded-xl shadow-2xl'
            }
          });
          console.error(error);
        }
      }
    });
  };

  return (
    <Disclosure as="nav" className={`bg-hs-blue sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-lg' : ''}`}>
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-white">
            <div className="relative flex h-16 items-center justify-between">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                {/* Mobile menu button */}
                <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-hs-dark-blue focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-all duration-200">
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon aria-hidden="true" className={`block h-6 w-6 transition-transform duration-200 ${open ? 'transform rotate-90 opacity-0' : 'opacity-100'}`} />
                  <XMarkIcon aria-hidden="true" className={`hidden h-6 w-6 transition-transform duration-200 ${open ? 'transform -rotate-90 opacity-100' : 'opacity-0'}`} />
                </DisclosureButton>
              </div>

              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex flex-shrink-0 items-center">
                  <Link to="/" className="flex items-center">
                    <h1 className='text-xl md:text-2xl text-hs-background font-bold tracking-tight hover:text-hs-background/90 transition-colors duration-200'>
                      ZACHOMATIC
                    </h1>
                  </Link>
                </div>
                <div className="hidden sm:flex flex-1 justify-center">
                  <div className="flex space-x-1 md:space-x-2">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={classNames(
                          isActive(item.href) 
                            ? 'bg-hs-background text-hs-blue' 
                            : 'text-hs-background/90 hover:bg-hs-dark-blue hover:text-hs-background',
                          'rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 ease-in-out relative',
                          'min-w-[90px] text-center'
                        )}
                      >
                        <span className="relative z-10">{item.name}</span>
                        {isActive(item.href) && (
                          <span className="absolute inset-0 bg-hs-background rounded-md shadow-sm z-0"></span>
                        )}
                      </Link>
                                         ))}
                   </div>
                 </div>
              </div>
              
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                <div className="hidden md:block mr-4">
                  <p className='text-sm text-hs-background/90 truncate max-w-[150px]'>{formatEmailDisplay(user.email)}</p>
                </div>

                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <MenuButton className="relative flex rounded-full bg-hs-dark-blue text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-hs-blue transition-all duration-200 hover:bg-hs-dark-blue/80">
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">Open user menu</span>
                      <img
                        alt=""
                        src="/Zach.png"
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    </MenuButton>
                  </div>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <MenuItem>
                      <button
                        type="button"
                        onClick={handleAccountSettings}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                      >
                        Account Settings
                      </button>
                    </MenuItem>
                    {user.franchisees && user.franchisees.length > 1 && (
                      <MenuItem>
                        <button
                          type="button"
                          onClick={() => navigate('/franchisee-selection')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                        >
                          Switch Account
                        </button>
                      </MenuItem>
                    )}
                    <MenuItem>
                      <button
                        type="button"
                        onClick={handleLogOut}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                      >
                        Sign out
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>

          <DisclosurePanel className="sm:hidden bg-hs-dark-blue/10 backdrop-blur-sm">
            <div className="space-y-1 px-2 pb-3 pt-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={classNames(
                    isActive(item.href) 
                      ? 'bg-hs-background text-hs-blue' 
                      : 'text-hs-background hover:bg-hs-dark-blue hover:text-hs-background',
                    'block rounded-md px-3 py-2 text-base font-medium transition-all duration-200 ease-in-out',
                    'relative'
                  )}
                >
                  <span className="relative z-10">{item.name}</span>
                  {isActive(item.href) && (
                    <span className="absolute inset-0 bg-hs-background rounded-md shadow-sm z-0"></span>
                  )}
                </Link>
                             ))}
               <div className="border-t border-hs-background/20 my-2 pt-2">
                <p className="px-3 py-2 text-sm text-hs-background/80 truncate">{formatEmailDisplay(user.email)}</p>
              </div>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}
